/**
 * useRelationshipDetail - Fetches co-mentions and interactions for a pair.
 *
 * Uses api.searchBehavioralDocuments() with both person hashes.
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';

function useRelationshipDetail(entityId, sourceHash, targetHash) {
  const [state, setState] = useState({
    coMentions: [],
    interactions: [],
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!entityId || !sourceHash || !targetHash) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Search for chunks mentioning both people via vector similarity search.
      // Query with both hashes to find co-mention evidence.
      // The SNA search endpoint doesn't support person hash filtering directly,
      // so we use semantic search with both identifiers.
      const result = await api.querySNAVectorStore({
        query: `${sourceHash} ${targetHash}`,
        entityId,
        limit: 100,
        includeContent: true,
      });

      const chunks = result?.results || result?.chunks || [];

      // Split into co-mentions (both appear) vs interactions (direct exchange indicators)
      // For MVP, we use a simple heuristic: chunks with dialogue indicators are "interactions"
      const interactions = [];
      const coMentions = [];

      chunks.forEach((chunk) => {
        const text = (chunk.content || chunk.text || '').toLowerCase();
        const isInteraction = hasInteractionIndicators(text);

        if (isInteraction) {
          interactions.push(chunk);
        }
        coMentions.push(chunk); // All chunks are co-mentions
      });

      setState({
        coMentions,
        interactions,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('useRelationshipDetail error:', err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err,
      }));
    }
  }, [entityId, sourceHash, targetHash]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    retry: fetchData,
  };
}

/**
 * Simple heuristic: check if text has dialogue/interaction patterns.
 */
function hasInteractionIndicators(text) {
  const indicators = [
    'said to',
    'told',
    'asked',
    'replied',
    'responded',
    'conversation between',
    'discussed with',
    'meeting with',
    'called',
    'emailed',
  ];
  return indicators.some((indicator) => text.includes(indicator));
}

export default useRelationshipDetail;
